/**
 * Trip Amount Round-off Logic
 *
 * This function implements the round-off logic for trip amounts based on configurable rules.
 * It ignores decimal parts and rounds based on the last digit(s) of the integer part.
 *
 * @param {number} amount - The original trip amount
 * @param {object} settings - Round-off settings containing:
 *   - roundBy: 'last_digit' or 'last_two_digits' or 'last_three_digits'
 *   - thresholdAmount: The threshold value for comparison
 *   - adjustToAmount or adjustmentToMount: The amount to add when threshold is met
 * @returns {number} The rounded amount
 */
export const applyRoundOffLogic = (amount, settings) => {
    // Return original amount if no settings provided
    if (!settings || !settings.roundBy || !settings.thresholdAmount) {
        console.log(
            "RoundOff: Missing settings, returning original amount:",
            amount
        );
        return amount;
    }

    // Handle both parameter naming conventions
    const adjustTo = settings.adjustToAmount || settings.adjustmentToMount;

    if (!adjustTo) {
        console.log(
            "RoundOff: Missing adjustTo amount, returning original amount:",
            amount
        );
        return amount;
    }

    // Extract the integer part (ignore decimals)
    const integerAmount = Math.floor(amount);
    console.log("RoundOff: Integer amount:", integerAmount);

    // Get the last digit(s) based on settings
    let lastDigits;
    if (settings.roundBy === "last_digit") {
        lastDigits = integerAmount % 10;
    } else if (settings.roundBy === "last_two_digits") {
        lastDigits = integerAmount % 100;
    } else if (settings.roundBy === "last_three_digits") {
        lastDigits = integerAmount % 1000;
    } else {
        console.log("RoundOff: Invalid roundBy setting:", settings.roundBy);
        return amount;
    }

    const threshold = settings.thresholdAmount;

    console.log("RoundOff Settings:", {
        originalAmount: amount,
        integerAmount,
        lastDigits,
        threshold,
        adjustTo,
        roundBy: settings.roundBy,
    });

    // CORRECTED LOGIC: Round DOWN to nearest 100 (or 10) first, then add adjustTo if threshold is met
    let roundedAmount;

    // Step 1: Round DOWN to nearest 1000, 100, or 10 depending on roundBy setting
    let baseRound;
    if (settings.roundBy === "last_digit") {
        baseRound = Math.floor(integerAmount / 10) * 10;
    } else if (settings.roundBy === "last_two_digits") {
        baseRound = Math.floor(integerAmount / 100) * 100;
    } else {
        // last_three_digits
        baseRound = Math.floor(integerAmount / 1000) * 1000;
    }

    // Step 2: Apply threshold logic
    if (lastDigits >= threshold) {
        // Add adjustTo to the base rounded amount
        roundedAmount = baseRound + adjustTo;
        console.log(
            "RoundOff: Last digits >= threshold, adding adjustTo to base round"
        );
    } else {
        // Use base rounded amount
        roundedAmount = baseRound;
        console.log("RoundOff: Last digits < threshold, using base round only");
    }

    console.log("RoundOff Result:", {
        originalAmount: amount,
        baseRound,
        roundedAmount,
        calculation:
            lastDigits >= threshold
                ? `Base round: ${baseRound}, then add adjustTo: ${baseRound} + ${adjustTo} = ${roundedAmount}`
                : `Base round: ${baseRound}, no adjustment needed = ${roundedAmount}`,
    });

    return roundedAmount;
};

/**
 * Check if round-off is enabled and apply the logic
 *
 * @param {number} amount - The original trip amount
 * @param {object} addonsData - Add-ons data from Redux store
 * @param {object} settingsData - Settings data containing round-off configuration
 * @returns {number} The rounded amount if enabled, otherwise original amount
 */
export const applyRoundOffIfEnabled = (amount, addonsData) => {
    // Check if addons data exists and has the trip_amount_roundoff addon
    const tripRoundoffAddon =
        addonsData && addonsData.data && addonsData.data.trip_amount_roundoff;

    // Check if the addon is enabled
    const isEnabled = tripRoundoffAddon && tripRoundoffAddon.enabled;

    if (!isEnabled) {
        console.log(
            "RoundOff: Feature disabled or not found, returning original amount:",
            amount
        );
        return amount;
    }

    // Get the settings from the addon
    const roundOffSettings = tripRoundoffAddon;

    if (
        !roundOffSettings ||
        !roundOffSettings.roundBy ||
        !roundOffSettings.thresholdAmount
    ) {
        console.log(
            "RoundOff: Invalid settings found, returning original amount:",
            amount,
            "Settings:",
            roundOffSettings
        );
        return amount;
    }

    console.log(
        "RoundOff: Applying round-off logic with settings:",
        roundOffSettings
    );
    return applyRoundOffLogic(amount, roundOffSettings);
};

/**
 * Get example descriptions in multiple languages
 */
export const getExamples = (lang = "en") => {
    const examples = {
        en: {
            title: "=== ROUND-OFF LOGIC TEST CASES ===",
            userTestCase: "--- User Test Case: 2662 with adjustTo=300 ---",
            originalBugReport: "--- Original Bug Report Test Cases ---",
            threeDigitsTests: "--- Three Digits Test Cases ---",
            summary: "--- SUMMARY ---",
            correctedLogic: "✅ CORRECTED LOGIC IMPLEMENTED:",
            step1: "1. Round DOWN to nearest 1000/100/10 first",
            step2: "2. If last digits >= threshold, ADD the adjustTo amount",
            step3: "3. If last digits < threshold, keep the base rounded amount",
            allTestsPass: "✅ ALL TEST CASES NOW PASS!",
            result: "Result",
            expected: "Expected",
            correct: "CORRECT!",
            wrong: "WRONG",
        },
        es: {
            title: "=== CASOS DE PRUEBA DE LÓGICA DE REDONDEO ===",
            userTestCase:
                "--- Caso de Prueba del Usuario: 2662 con adjustTo=300 ---",
            originalBugReport:
                "--- Casos de Prueba del Informe de Error Original ---",
            threeDigitsTests: "--- Casos de Prueba de Tres Dígitos ---",
            summary: "--- RESUMEN ---",
            correctedLogic: "✅ LÓGICA CORREGIDA IMPLEMENTADA:",
            step1: "1. Redondear HACIA ABAJO al 1000/100/10 más cercano primero",
            step2: "2. Si los últimos dígitos >= umbral, AGREGAR el monto adjustTo",
            step3: "3. Si los últimos dígitos < umbral, mantener el monto redondeado base",
            allTestsPass: "✅ ¡TODAS LAS PRUEBAS PASAN!",
            result: "Resultado",
            expected: "Esperado",
            correct: "¡CORRECTO!",
            wrong: "INCORRECTO",
        },
        id: {
            title: "=== KASUS UJI LOGIKA PEMBULATAN ===",
            userTestCase:
                "--- Kasus Uji Pengguna: 2662 dengan adjustTo=300 ---",
            originalBugReport: "--- Kasus Uji Laporan Bug Asli ---",
            threeDigitsTests: "--- Kasus Uji Tiga Digit ---",
            summary: "--- RINGKASAN ---",
            correctedLogic: "✅ LOGIKA YANG DIPERBAIKI DIIMPLEMENTASIKAN:",
            step1: "1. Bulatkan KE BAWAH ke 1000/100/10 terdekat terlebih dahulu",
            step2: "2. Jika digit terakhir >= ambang batas, TAMBAHKAN jumlah adjustTo",
            step3: "3. Jika digit terakhir < ambang batas, pertahankan jumlah dasar yang dibulatkan",
            allTestsPass: "✅ SEMUA KASUS UJI BERHASIL!",
            result: "Hasil",
            expected: "Diharapkan",
            correct: "BENAR!",
            wrong: "SALAH",
        },
    };

    return examples[lang] || examples.en;
};

/**
 * Example usage and test cases
 */
export const testRoundOffLogic = (lang = "en") => {
    const t = getExamples(lang);
    console.log(t.title);

    // User's specific test case
    console.log(`\n${t.userTestCase}`);
    const userSettings = {
        roundBy: "last_two_digits",
        thresholdAmount: 50,
        adjustmentToMount: 300, // Using Firebase parameter name
    };

    const userTest = applyRoundOffLogic(2662, userSettings);
    console.log(
        `${t.result}: ${userTest} (${t.expected}: 2900) - ${
            userTest === 2900 ? t.correct : t.wrong
        }`
    );

    // Original test cases from bug report
    console.log(`\n${t.originalBugReport}`);
    const settings = {
        roundBy: "last_two_digits",
        thresholdAmount: 50,
        adjustToAmount: 100,
    };

    console.log("\nTest 4112.4 with adjustTo=100:");
    const test4112 = applyRoundOffLogic(4112.4, settings);
    console.log(
        `${t.result}: ${test4112} (${t.expected}: 4100) - ${
            test4112 === 4100 ? t.correct : t.wrong
        }`
    );

    console.log("\nTest 4295.76 with adjustTo=100:");
    const test4295 = applyRoundOffLogic(4295.76, settings);
    console.log(
        `${t.result}: ${test4295} (${t.expected}: 4300) - ${
            test4295 === 4300 ? t.correct : t.wrong
        }`
    );

    // Test with different adjustTo values for 4112.4
    console.log("\nTest 4112.4 with adjustTo=200:");
    const settings200 = { ...settings, adjustToAmount: 200 };
    const test4112_200 = applyRoundOffLogic(4112.4, settings200);
    console.log(
        `${t.result}: ${test4112_200} (${t.expected}: 4100) - ${
            test4112_200 === 4100 ? t.correct : t.wrong
        }`
    );

    console.log("\nTest 4112.4 with adjustTo=300:");
    const settings300 = { ...settings, adjustToAmount: 300 };
    const test4112_300 = applyRoundOffLogic(4112.4, settings300);
    console.log(
        `${t.result}: ${test4112_300} (${t.expected}: 4100) - ${
            test4112_300 === 4100 ? t.correct : t.wrong
        }`
    );

    // Test with different adjustTo values for 4295.76
    console.log("\nTest 4295.76 with adjustTo=200:");
    const test4295_200 = applyRoundOffLogic(4295.76, settings200);
    console.log(
        `${t.result}: ${test4295_200} (${t.expected}: 4400) - ${
            test4295_200 === 4400 ? t.correct : t.wrong
        }`
    );

    console.log("\nTest 4295.76 with adjustTo=300:");
    const test4295_300 = applyRoundOffLogic(4295.76, settings300);
    console.log(
        `${t.result}: ${test4295_300} (${t.expected}: 4500) - ${
            test4295_300 === 4500 ? t.correct : t.wrong
        }`
    );

    // NEW: Three digits test cases
    console.log(`\n${t.threeDigitsTests}`);
    const threeDigitsSettings = {
        roundBy: "last_three_digits",
        thresholdAmount: 500,
        adjustToAmount: 1000,
    };

    console.log(
        "\nTest 12345.67 with last_three_digits, threshold=500, adjustTo=1000:"
    );
    const test12345 = applyRoundOffLogic(12345.67, threeDigitsSettings);
    console.log(
        `${t.result}: ${test12345} (${t.expected}: 12000) - ${
            test12345 === 12000 ? t.correct : t.wrong
        }`
    );
    console.log("Logic: last 3 digits = 345, 345 < 500, so baseRound = 12000");

    console.log(
        "\nTest 12678.90 with last_three_digits, threshold=500, adjustTo=1000:"
    );
    const test12678 = applyRoundOffLogic(12678.9, threeDigitsSettings);
    console.log(
        `${t.result}: ${test12678} (${t.expected}: 13000) - ${
            test12678 === 13000 ? t.correct : t.wrong
        }`
    );
    console.log(
        "Logic: last 3 digits = 678, 678 >= 500, so 12000 + 1000 = 13000"
    );

    console.log(
        "\nTest 9876.54 with last_three_digits, threshold=800, adjustTo=2000:"
    );
    const threeDigitsSettings2 = {
        ...threeDigitsSettings,
        thresholdAmount: 800,
        adjustToAmount: 2000,
    };
    const test9876 = applyRoundOffLogic(9876.54, threeDigitsSettings2);
    console.log(
        `${t.result}: ${test9876} (${t.expected}: 11000) - ${
            test9876 === 11000 ? t.correct : t.wrong
        }`
    );
    console.log(
        "Logic: last 3 digits = 876, 876 >= 800, so 9000 + 2000 = 11000"
    );

    console.log(`\n${t.summary}`);
    console.log(t.correctedLogic);
    console.log(t.step1);
    console.log(t.step2);
    console.log(t.step3);
    console.log(`\n${t.allTestsPass}`);

    return {
        userTest: userTest === 2900,
        test4112: test4112 === 4100,
        test4295: test4295 === 4300,
        test4112_200: test4112_200 === 4100,
        test4112_300: test4112_300 === 4100,
        test4295_200: test4295_200 === 4400,
        test4295_300: test4295_300 === 4500,
        test12345: test12345 === 12000,
        test12678: test12678 === 13000,
        test9876: test9876 === 11000,
        allPassed:
            userTest === 2900 &&
            test4112 === 4100 &&
            test4295 === 4300 &&
            test4112_200 === 4100 &&
            test4112_300 === 4100 &&
            test4295_200 === 4400 &&
            test4295_300 === 4500 &&
            test12345 === 12000 &&
            test12678 === 13000 &&
            test9876 === 11000,
    };
};

export default {
    applyRoundOffLogic,
    applyRoundOffIfEnabled,
    testRoundOffLogic,
    getExamples,
};
